import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { useAuth } from "@/hooks/useAuth";
import { isSupabaseConfigured } from "@/lib/supabase";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { Store, AlertCircle } from "lucide-react";

const AuthLoginForm = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();

  if (!isSupabaseConfigured) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-heading font-bold mb-4">Login Unavailable</h2>
        <p className="text-muted-foreground">Supabase authentication is not configured. Please check your environment variables.</p>
      </div>
    );
  }

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData(e.currentTarget);
      const email = String(form.get("email") || "").trim();
      const password = String(form.get("password") || "");

      if (!email || !password) {
        toast.error("Please fill in all fields");
        return;
      }

      console.log("Attempting customer login for:", email);

      const { data, error } = await signIn(email, password);

      if (error) {
        console.error("Login error:", error);

        // Provide user-friendly error messages
        let userMessage = "Login failed";
        if (error.message.includes("Invalid login credentials")) {
          userMessage = "Invalid email or password. Please check your credentials and try again.";
        } else if (error.message.includes("Email not confirmed")) {
          userMessage = "Please check your email and click the verification link before signing in.";
        } else if (error.message.includes("Too many requests")) {
          userMessage = "Too many login attempts. Please wait a moment and try again.";
        } else if (error.message.includes("User not found")) {
          userMessage = "No account found with this email. Please register first.";
        } else {
          userMessage = error.message || "Login failed";
        }

        toast.error(userMessage);
        return;
      }

      console.log("Login successful:", data);

      // Check user metadata for role
      const userRole = data.user?.user_metadata?.role;

      if (userRole === "seller") {
        // If this is a seller account, suggest they use seller login
        toast.error("This is a seller account. Please use the seller login page.");
        await signOut(); // Sign them out so they can use the correct login

        setTimeout(() => {
          navigate("/seller/login");
        }, 2000);
        return;
      }

      toast.success("Welcome back!");

      // Redirect customers to products page to start shopping
      navigate("/products");

    } catch (err: any) {
      console.error("Unexpected login error:", err);
      toast.error("An unexpected error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <h2 className="text-2xl font-heading font-bold mb-2">Sign in to your account</h2>

      {/* Seller Login Alert */}
      <Alert className="border-primary/20 bg-primary/5">
        <Store className="h-4 w-4" />
        <AlertDescription>
          Looking to access your seller dashboard?{" "}
          <Link to="/seller/login" className="text-primary hover:underline font-medium">
            Use seller login instead →
          </Link>
        </AlertDescription>
      </Alert>

      {/* Email/Password Form */}
      <form className="flex flex-col gap-4 w-full" onSubmit={onSubmit}>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input 
            required 
            id="email" 
            name="email" 
            type="email" 
            placeholder="you@email.com" 
            className="mt-1" 
            disabled={loading}
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link 
              to="/forgot-password" 
              className="text-sm text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input 
            required 
            id="password" 
            name="password" 
            type="password" 
            placeholder="••••••••" 
            className="mt-1" 
            disabled={loading}
          />
        </div>
        <Button 
          size="lg" 
          className="mt-2" 
          type="submit" 
          disabled={loading}
        >
          {loading ? "Signing in..." : "Sign In"}
        </Button>
        <div className="text-center">
          <button 
            type="button" 
            className="text-sm text-muted-foreground hover:text-foreground underline" 
            onClick={() => navigate("/register")}
            disabled={loading}
          >
            Don't have an account? Create one
          </button>
        </div>
      </form>
    </div>
  );
};
export default AuthLoginForm;